import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Shield, Loader2 } from "lucide-react";
import { usePartnerBranding } from "../hooks/usePartnerBranding";

export default function Login() {
  const { user, login, loginVerify2fa } = useAuth();
  const navigate = useNavigate();
  const branding = usePartnerBranding();
  const [email, setEmail] = useState("admin@fortiqa.co.ke");
  const [password, setPassword] = useState("Admin@123");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [totpCode, setTotpCode] = useState("");

  if (user && typeof user === "object") {
    const home = (user.role === "merchant" || user.role === "merchant_staff") ? "/merchant"
      : (user.role === "partner_admin" || user.role === "partner_analyst" || user.role === "partner_viewer") ? "/partner"
      : "/app";
    return <Navigate to={home} replace />;
  }

  function goHome(u) {
    const home = (u?.role === "merchant" || u?.role === "merchant_staff") ? "/merchant"
      : (u?.role === "partner_admin" || u?.role === "partner_analyst" || u?.role === "partner_viewer") ? "/partner"
      : "/app";
    navigate(home, { replace: true });
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const u = await login(email, password);
      if (u && u.two_factor_required) {
        setChallenge(u.challenge_token);
        return;
      }
      goHome(u);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmit2fa(e) {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const u = await loginVerify2fa(challenge, totpCode);
      goHome(u);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left brand panel */}
      <div
        className="hidden lg:flex lg:w-1/2 relative"
        style={{
          backgroundImage:
            "linear-gradient(rgba(17,66,50,0.78), rgba(17,66,50,0.92)), url('https://images.unsplash.com/photo-1761370980657-22586ea44093?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="m-auto p-12 max-w-lg text-[#f4f2eb]">
          <div className="flex items-center gap-3 mb-12">
            <img src="/fortiqa-mark.svg" alt="Fortiqa" className="w-12 h-12 object-contain" />
            <div className="font-heading text-2xl font-bold tracking-tight">Fortiqa</div>
          </div>
          <h1 className="font-heading text-4xl xl:text-5xl font-bold leading-[1.05] mb-6">
            Cyber cover for every <span className="text-[#d49a36]">Kenyan merchant.</span>
          </h1>
          <p className="text-[#f4f2eb]/85 text-base leading-relaxed mb-8">
            Embedded micro-insurance that protects against M-Pesa fraud, account
            takeovers, phishing, and POS settlement loss — billed per week, paid in seconds.
          </p>
          <div className="space-y-3 text-sm text-[#f4f2eb]/85">
            <div className="flex items-center gap-2"><span className="text-[#d49a36]">●</span> KES 99 / week starting tier</div>
            <div className="flex items-center gap-2"><span className="text-[#d49a36]">●</span> 7-day cooling-off, IRA-aligned</div>
            <div className="flex items-center gap-2"><span className="text-[#d49a36]">●</span> WhatsApp-first onboarding</div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#f4f2eb]">
        {challenge ? (
          <form onSubmit={onSubmit2fa} className="w-full max-w-sm space-y-6" data-testid="login-2fa-form">
            <div className="space-y-1">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#c15337] font-bold">Two-factor</div>
              <h2 className="font-heading text-3xl font-bold text-[#114232]">Enter your code</h2>
              <p className="text-sm text-[#5c6360]">
                Open your authenticator app and enter the 6-digit code for {email}.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="totp-code">Authentication code</Label>
              <Input
                id="totp-code" autoFocus inputMode="numeric" pattern="[0-9]{6,8}"
                required maxLength={8} placeholder="123456"
                value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                data-testid="login-totp-input"
                className="bg-white border-[#114232]/15 text-center tracking-[0.3em] font-mono text-lg"
              />
            </div>
            {err && (
              <div data-testid="login-2fa-error" className="text-sm text-[#c15337] bg-[#c15337]/8 border border-[#c15337]/20 px-3 py-2 rounded-md">
                {err}
              </div>
            )}
            <Button type="submit" disabled={submitting || totpCode.length < 6}
                       className="w-full btn-baobab h-11 rounded-full hover:bg-[var(--baobab-hover)]"
                       data-testid="login-2fa-submit">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Verify code
            </Button>
            <button type="button" onClick={() => { setChallenge(null); setTotpCode(""); setErr(""); }}
                      className="block mx-auto text-xs text-[#5c6360] underline hover:text-[#114232]"
                      data-testid="login-2fa-back">
              Back to password
            </button>
          </form>
        ) : (
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="space-y-1">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#c15337] font-bold">
              {branding.name ? `${branding.name} · Powered by Fortiqa` : "Fortiqa Portal"}
            </div>
            <h2 className="font-heading text-3xl font-bold" style={branding.primary ? { color: branding.primary } : { color: "#114232" }}>
              Welcome back
            </h2>
            <p className="text-sm text-[#5c6360]">Sign in to manage your cover & claims.</p>
            {branding.partner && (
              <div className="flex items-center gap-2 mt-2" data-testid="partner-branding-pill">
                {branding.logoUrl ? (
                  <img src={branding.logoUrl} alt={branding.name} className="h-6 w-auto" />
                ) : (
                  <div className="w-6 h-6 rounded" style={{ backgroundColor: branding.primary }} />
                )}
                <span className="text-[10px] uppercase tracking-wider font-semibold text-[#5c6360]">
                  {branding.name}
                </span>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email" type="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              placeholder="admin@fortiqa.co.ke"
              className="bg-white border-[#114232]/15 focus-visible:ring-[#114232]/20"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password" type="password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="bg-white border-[#114232]/15 focus-visible:ring-[#114232]/20"
            />
          </div>
          {err && (
            <div data-testid="login-error" className="text-sm text-[#c15337] bg-[#c15337]/8 border border-[#c15337]/20 px-3 py-2 rounded-md">
              {err}
            </div>
          )}
          <Button
            type="submit" disabled={submitting}
            className="w-full btn-baobab h-11 rounded-full hover:bg-[var(--baobab-hover)]"
            data-testid="login-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Sign in
          </Button>
          <div className="pt-4 border-t border-[#114232]/10">
            <details className="group" data-testid="login-demo-disclosure">
              <summary className="cursor-pointer text-xs text-[#5c6360] hover:text-[#114232] inline-flex items-center gap-1.5 select-none transition-colors">
                <span className="text-[#c15337] font-bold">⌘</span>
                <span>Try a demo account</span>
                <span className="text-[#5c6360]/60 group-open:hidden">→</span>
                <span className="text-[#5c6360]/60 hidden group-open:inline">↓</span>
              </summary>
              <div className="mt-3 space-y-2">
                {[
                  ["admin@fortiqa.co.ke", "Admin@123", "Admin"],
                  ["wanjiru@example.co.ke", "Merchant@123", "Merchant"],
                ].map(([e, p, role]) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => {
                      setEmail(e);
                      setPassword(p);
                    }}
                    data-testid={`login-demo-${role.toLowerCase()}`}
                    className="w-full text-left flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white border border-[#114232]/10 hover:border-[#114232]/30 hover:bg-[#114232]/2 transition-all"
                  >
                    <div>
                      <div className="text-xs font-semibold text-[#114232]">{role}</div>
                      <div className="text-[10px] text-[#5c6360] font-mono mt-0.5">{e}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-[#c15337] font-bold">
                      Use
                    </span>
                  </button>
                ))}
              </div>
            </details>
            <div className="mt-4 text-xs text-[#5c6360]">
              New here?{" "}
              <Link
                to={branding.slug ? `/signup?partner=${branding.slug}` : "/signup"}
                data-testid="login-to-signup"
                className="font-semibold text-[#114232] hover:text-[#0d3125] underline-offset-2 hover:underline"
              >
                Create a merchant account
              </Link>
            </div>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
